/**
 * Temporary shared types for Bucket 10 (utils/server/api/pages/context/tracking/hooks).
 * Follow-up: merge into the broader type system after Phase 3.
 */

import type { CurrentUserStatusValue } from '@didomi/react';
import type { QueryOptions } from 'Utils/graphql-client/graphqlClient';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';

import type { ComponentType } from 'react';
import type { NextAppContext } from 'Src/types/next-serverside';

type ChildcareFacetInput = { label: string; value: string };
type PromoCodeParams = {
  proposalId: string;
  promoCode: string;
  type: 'apply' | 'remove';
  partnerId?: string;
  accessCode?: string;
  customerId?: number;
};

/** ABTasty test entry returned by `getTestsOnPage()`. */
export type ABTastyTestOnPage = {
  variationID?: number | string;
  [key: string]: unknown;
};

export type ABTastyEventState = {
  tagContentExecuted?: {
    status?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

/** Minimal Didomi SDK surface used by DidomiBanner. */
export type DidomiSdkInstance = {
  setUserAgreeToAll: () => void;
  notice: { hide: () => void };
  getCurrentUserStatus: () => { vendors: Record<string, CurrentUserStatusValue<string>> };
};

export type DidomiBannerConfig = {
  organizationUserId?: string;
  externalConsent: { enabled: boolean };
  apiPath: string;
};

export type ConsentUrls = {
  sdkPath: string;
  apiPath: string;
};

/** Deep-partial domain entities for lodash `get()` helpers (production). */
export type ProposalLike = Partial<Proposal>;
export type StayEntityLike = Partial<StayEntity>;

/** Cast minimal unit-test fixtures without structural checks on nested keys. */
export const asProposalLike = (fixture: object): ProposalLike => fixture as ProposalLike;
export const asProposal = (fixture: object): Proposal => fixture as unknown as Proposal;
export const asAccommodation = (fixture: object): Accommodation =>
  fixture as unknown as Accommodation;
export const asStayEntityLike = (fixture: object): StayEntityLike => fixture as StayEntityLike;
export const asStayEntity = (fixture: object): StayEntity => fixture as StayEntity;
export const asAppContext = (partial: Partial<AppContext>): AppContext => partial as AppContext;
export const asProposals = (fixtures: object[]): Proposal[] => fixtures as unknown as Proposal[];
export const asTransport = (fixture: object): Transport => fixture as unknown as Transport;
export const asKids = (fixtures: object[]): Kids[] => fixtures as unknown as Kids[];
export const asChildcareFacetInput = (fixtures: object[]): ChildcareFacetInput[] =>
  fixtures as unknown as ChildcareFacetInput[];
export const asSearchFacetCategories = (fixtures: object[]): SearchFacetCategory[] =>
  fixtures as unknown as SearchFacetCategory[];
export const asProduct = (fixture: object): Product => fixture as unknown as Product;
export const asKid = (fixture: object): Kids => fixture as unknown as Kids;
export const asAccommodationsSet = (fixture: object): AccommodationsSet =>
  fixture as unknown as AccommodationsSet;
export const asTravelSectionBound = (fixture: object): TravelSectionBound =>
  fixture as unknown as TravelSectionBound;
export const asBookingVendor = (fixture: object): BookingVendor =>
  fixture as unknown as BookingVendor;
export const asPromoCodeParams = (fixture: PromoCodeParams): PromoCodeParams => fixture;

/** Cast appConfig.get() sections with unknown shape. */
export const asAppConfigSection = <T>(value: unknown): T => value as T;

/** Cast session-storage values read as JSON/string for cacheFront. */
export const asSessionStorageJson = (value: unknown): string =>
  (typeof value === 'string' ? value : '{}') as string;

/** Cast lead-info score from session storage. */
export const asLeadInfoScore = (value: unknown): string | null =>
  value != null ? String(value) : null;
export const asPageContext = (partial: Partial<PageContext>): PageContext => partial as PageContext;

/** PageContext test fixtures may include legacy `uri` (pre-canonicalUrl migration). */
export type PageContextWithUri = Partial<PageContext> & { uri?: string };
export const asPageContextWithUri = (partial: PageContextWithUri): PageContext =>
  partial as PageContext;

/** CMS menu fixtures in i18n.ts include nested subItems not yet on CMSMenuItem. */
export type CMSMenuItemWithSubItems = CMSMenuItem & {
  subItems?: CMSMenuItemWithSubItems[];
};

/** Minimal QueryOptions for graphqlClient specs (requestName required). */
export const asQueryOptions = (partial: Partial<QueryOptions> = {}): QueryOptions =>
  ({
    requestName: '',
    queryId: '',
    prebuiltArgs: {},
    strictErrors: true,
    ...partial,
  }) as QueryOptions;

/** Provider callback payload parsed from payment redirect query/body. */
export type PaymentProviderResponse = {
  bnpl?: string;
  isManual?: boolean;
  currency?: string;
  value?: string | number;
  paymentStatus?: string;
  bookingId?: string;
  providerId?: string;
  [key: string]: unknown;
};

/** GQL bookmark/search payloads mapped by favorite.utils (untyped upstream). */
export type FavoriteMapperInput = Record<string, unknown>;

/** Page component metadata used by pages/_app.tsx. */
export type CMPageComponent = ComponentType<Record<string, unknown>> & {
  pageId?: string;
  pageType?: string;
  requireAuthentication?: boolean;
  getCMInitialProps?: (ctx: unknown) => Promise<Record<string, unknown>>;
  disableHeader?: boolean | ((appContext: AppContext) => boolean);
  disableFooter?: boolean | ((appContext: AppContext) => boolean);
};

export const asCMPageComponent = (component: NextAppContext['Component']): CMPageComponent =>
  component as CMPageComponent;

/** Minimal URI surface used by useSetUrlWithFacetings. */
export type FacetingUri = {
  query: (decode?: boolean) => Record<string, string | undefined>;
  clone: () => FacetingUri;
  setQuery: (query: Record<string, unknown>) => FacetingUri;
  hash: (hash: string | null) => FacetingUri;
  toString: () => string;
};

/** Normalize Next.js query param values to a single string. */
export const queryParamToString = (
  value: string | readonly string[] | undefined | void,
): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value[0];
  }
  return typeof value === 'string' ? value : undefined;
};

/** Club Med Next.js page component with static metadata hooks. */
export type CMNextPageComponent = ComponentType<Record<string, unknown>> & {
  pageId?: string;
  pageType?: string;
  requireAuthentication?: boolean;
  disableHeader?: boolean | ((appContext: AppContext) => boolean);
  disableFooter?: boolean | ((appContext: AppContext) => boolean);
  getCMInitialProps?: (
    ctx: import('Src/types/common/common').GetInitialPropsContext,
  ) => Promise<Record<string, unknown>>;
};

declare global {
  namespace jest {
    interface Matchers<R> {
      toHaveBeenCalledWithQuery(...expected: unknown[]): R;
    }
  }
}

/** GraphQL client options extended with cookie forwarding (server payment flows). */
export type QueryOptionsWithCookies = QueryOptions & {
  cookies?: Record<string, unknown>;
};

/** Manual payment notify payload (isManual branch in paymentRedirectUtils). */
export type ManualProviderResponse = PaymentProviderResponse & {
  isManual: true;
};

/** approvePayment mutation response used by payment redirect handlers. */
export type ApprovePaymentResponse = {
  approvePayment: {
    providerResponse: string;
    htmlAnswer?: string | null;
  };
};

/** Cookies bag forwarded to payment status GraphQL mutations. */
export type PaymentRedirectCookies = Record<string, string | undefined> & {
  bookingQueryParams?: string;
};

/** Minimal request shape for extractLocale in paymentRedirectUtils. */
export type PaymentRedirectUtilsReq = {
  readonly query: { locale?: string; l?: string };
  readonly params: { locale?: string };
  hostname: string;
};

export const asPaymentRedirectUtilsReq = (
  partial: Partial<PaymentRedirectUtilsReq>,
): PaymentRedirectUtilsReq => ({
  query: {},
  params: {},
  hostname: 'clubmed.com',
  ...partial,
});

/** Request shape for extractProviderResponse. */
export type ExtractProviderResponseRequest = {
  query?: Record<string, unknown>;
  body?: Record<string, unknown>;
};

/** CMS line resolver surface used by dynamic-page fetchLine. */
export type CmsLineResolver = {
  resolve: (
    appContext: AppContext,
    cmsProps: Record<string, unknown>,
    pageContext: PageContext,
    location: RouterLocation,
  ) => Promise<Record<string, unknown>>;
};

/** Decoded URI query parameters from @clubmed/utils url helpers. */
export type DecodedQueryParameters = Record<string, string | undefined>;

/** GQL paymentNotify fixture with extra fields returned by the API in tests. */
export type PaymentNotifyMock = PaymentNotify & {
  currency?: string;
  paymentAmount?: PaymentNotify['paymentAmount'] & { formattedValue?: string };
};

export const asPaymentNotifyResponse = (fixture: {
  paymentNotify: PaymentNotifyMock;
}): { paymentNotify: PaymentNotify } => fixture as { paymentNotify: PaymentNotify };

/** Generic fixture cast for unit tests. */
export const castAs = <T>(value: object): T => value as unknown as T;
